import * as i0 from '@angular/core';
import { EventEmitter, inject, Output, Input, ViewChild, Component, HostListener, Directive, NgModule } from '@angular/core';
import * as i4 from '@engineering11/ui-lib/e11-ui-utils';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';
import * as i1 from '@engineering11/web-utilities';
import * as i3 from '@engineering11/ui-lib/e11-modal';
import { E11ModalModule } from '@engineering11/ui-lib/e11-modal';
import * as i5 from '@engineering11/ui-lib/e11-input-checkbox';
import { E11InputCheckboxModule } from '@engineering11/ui-lib/e11-input-checkbox';
import * as i6 from '@engineering11/ui-lib/e11-button';
import { E11ButtonModule } from '@engineering11/ui-lib/e11-button';
import { CommonModule } from '@angular/common';

class BaseE11ExternalLinkConfigProvider {
}

class E11ExternalLinkComponent {
    constructor(localStorageService, config) {
        this.localStorageService = localStorageService;
        this.config = config;
        this.closed = new EventEmitter();
        this.urlParts = { before: '', hostname: '', after: '' };
        this.isBrowser = inject(IS_BROWSER);
    }
    ngOnInit() {
        this.urlParts = this.splitUrl();
    }
    ngAfterViewInit() {
        this.openLink();
    }
    openLink() {
        if (!this.isBrowser)
            return;
        if (!this.isDomainTrusted()) {
            this.modal.open();
            return;
        }
        window.open(this.href, '_blank');
    }
    confirmExit(confirm, shouldTrust) {
        if (!this.isBrowser)
            return;
        if (confirm) {
            this.setTrustedDomains(shouldTrust);
            window.open(this.href, '_blank');
        }
        this.modal.close();
    }
    setTrustedDomains(trustDomain) {
        if (!trustDomain || !this.isBrowser) {
            return;
        }
        const domain = this.urlParts.hostname;
        const trustedDomains = this.trustedDomains();
        trustedDomains.push(domain);
        this.localStorageService.setItem('trustedDomains', trustedDomains);
    }
    trustedDomains() {
        if (!this.isBrowser)
            return null;
        return Object.values(this.localStorageService.getItem('trustedDomains') ?? []);
    }
    isDomainTrusted() {
        return this.trustedDomains().includes(this.urlParts.hostname);
    }
    splitUrl() {
        const regex = /^(https?:\/\/(?:www\.)?)([^\/\?#]+)(.*)$/;
        const match = this.href.match(regex);
        if (!match) {
            return {
                before: '',
                hostname: '',
                after: '',
            };
        }
        return {
            before: match[1] || '',
            hostname: match[2] || '',
            after: match[3] || '',
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkComponent, deps: [{ token: i1.BaseLocalStorageService }, { token: BaseE11ExternalLinkConfigProvider }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11ExternalLinkComponent, selector: "ng-component", inputs: { href: "href" }, outputs: { closed: "closed" }, viewQueries: [{ propertyName: "modal", first: true, predicate: ["modal"], descendants: true }], ngImport: i0, template: `
    <e11-modal #modal [width]="'sm'" [showHeader]="false" (modalClosed)="closed.emit($event)">
      <ng-template e11Template="Body">
        <div class="e11-w-full">
          <h2 class="e11-text-lg e11-font-bold e11-mb-4">You are leaving {{ config.config.siteTitle }}</h2>
          <p class="e11-mb-2">This link is for an external site and will take you away from {{ config.config.siteTitle }} to the following URL:</p>
          <div class="e11-bg-skin-grey/20 e11-p-2 e11-rounded e11-mb-4 e11-overflow-x-auto e11-whitespace-nowrap">
            <span>{{ urlParts.before }}</span>
            <span class="e11-font-bold">{{ urlParts.hostname }}</span>
            <span>{{ urlParts.after }}</span>
          </div>

          <div class="e11-mb-8">
            <e11-input-checkbox
              #trustDomain
              [color]="'primary-accent'"
              [size]="'md'"
              [ring]="true"
              name="checkbox"
              [label]="'Trust ' + urlParts.hostname + ' links from now on'"
              [checked]="false"
              checkboxPosition="left"
            ></e11-input-checkbox>
          </div>

          <div class="e11-flex e11-justify-end e11-gap-2">
            <e11-button
              [value]="'Go Back'"
              size="md"
              [ghost]="true"
              [color]="'neutral'"
              (click)="confirmExit(false, false)"
              [buttonStyle]="'pill'"
            ></e11-button>

            <e11-button
              [value]="'Continue'"
              size="md"
              [color]="'primary-accent'"
              [buttonStyle]="'pill'"
              (click)="confirmExit(true, trustDomain._checked ?? false)"
            ></e11-button>
          </div>
        </div>
      </ng-template>
    </e11-modal>
  `, isInline: true, dependencies: [{ kind: "component", type: i3.E11ModalComponent, selector: "e11-modal", inputs: ["title", "width", "bodyHeight", "titleAlign", "hideCloseButton", "closer", "bodyClassOverrides", "bodyClass", "modalClassOverrides", "modalClass", "contentClassOverrides", "contentClass", "headerClassOverrides", "headerClass", "footerClassOverrides", "footerClass", "showFooter", "showHeader", "darkMode", "closeOnEscape", "closeOnClickOutside", "headerCTAs", "hasBackdrop", "backdropStyle"], outputs: ["modalOpened", "onModalOpened", "modalClosed", "onModalClosed", "headerCTAClicked", "onHeaderCTAClicked"] }, { kind: "directive", type: i4.E11Template, selector: "[e11Template]", inputs: ["type", "e11Template"] }, { kind: "component", type: i5.E11CheckboxComponent, selector: "e11-input-checkbox", inputs: ["name", "size", "color", "fontColor", "ring", "inputClass", "containerClass", "label", "display", "description", "descriptionClassOverrides", "checked", "checkboxPosition", "disabled", "disabledText", "containerClassOverrides", "labelClassOverrides", "loading", "e11CheckboxProps"] }, { kind: "component", type: i6.E11ButtonComponent, selector: "e11-button", inputs: ["color", "ghost", "fontColor", "id", "name", "type", "value", "size", "style", "buttonStyle", "loading", "skeletonLoading", "loadingText", "display", "buttonGroup", "paddingX", "icon", "iconType", "symbol", "symbolType", "iconSize", "iconPosition", "iconClassOverrides", "containerClassOverrides", "disabledText", "disabled", "titleText", "titleTextPosition", "autofocus"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkComponent, decorators: [{
            type: Component,
            args: [{
                    template: `
    <e11-modal #modal [width]="'sm'" [showHeader]="false" (modalClosed)="closed.emit($event)">
      <ng-template e11Template="Body">
        <div class="e11-w-full">
          <h2 class="e11-text-lg e11-font-bold e11-mb-4">You are leaving {{ config.config.siteTitle }}</h2>
          <p class="e11-mb-2">This link is for an external site and will take you away from {{ config.config.siteTitle }} to the following URL:</p>
          <div class="e11-bg-skin-grey/20 e11-p-2 e11-rounded e11-mb-4 e11-overflow-x-auto e11-whitespace-nowrap">
            <span>{{ urlParts.before }}</span>
            <span class="e11-font-bold">{{ urlParts.hostname }}</span>
            <span>{{ urlParts.after }}</span>
          </div>

          <div class="e11-mb-8">
            <e11-input-checkbox
              #trustDomain
              [color]="'primary-accent'"
              [size]="'md'"
              [ring]="true"
              name="checkbox"
              [label]="'Trust ' + urlParts.hostname + ' links from now on'"
              [checked]="false"
              checkboxPosition="left"
            ></e11-input-checkbox>
          </div>

          <div class="e11-flex e11-justify-end e11-gap-2">
            <e11-button
              [value]="'Go Back'"
              size="md"
              [ghost]="true"
              [color]="'neutral'"
              (click)="confirmExit(false, false)"
              [buttonStyle]="'pill'"
            ></e11-button>

            <e11-button
              [value]="'Continue'"
              size="md"
              [color]="'primary-accent'"
              [buttonStyle]="'pill'"
              (click)="confirmExit(true, trustDomain._checked ?? false)"
            ></e11-button>
          </div>
        </div>
      </ng-template>
    </e11-modal>
  `,
                }]
        }], ctorParameters: () => [{ type: i1.BaseLocalStorageService }, { type: BaseE11ExternalLinkConfigProvider }], propDecorators: { modal: [{
                type: ViewChild,
                args: ['modal']
            }], href: [{
                type: Input,
                args: [{ required: true }]
            }], closed: [{
                type: Output
            }] } });

class E11ExternalLinkDirective {
    constructor(viewContainerRef, el) {
        this.viewContainerRef = viewContainerRef;
        this.el = el;
    }
    onClick(event) {
        const url = this.el.nativeElement.getAttribute('href');
        if (!this.isExternalUrl(url)) {
            return;
        }
        event.preventDefault();
        this.showConfirmationModal(url);
    }
    isExternalUrl(url) {
        const externalUrlHost = new URL(url).hostname;
        const currentUrlHost = window.location.hostname;
        return externalUrlHost !== currentUrlHost;
    }
    showConfirmationModal(url) {
        const modalRef = this.viewContainerRef.createComponent(E11ExternalLinkComponent);
        modalRef.instance.href = url;
        modalRef.instance.closed.subscribe(() => {
            modalRef.destroy();
        });
        // trigger change detection
        modalRef.hostView.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkDirective, deps: [{ token: i0.ViewContainerRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: E11ExternalLinkDirective, selector: "a[e11ExternalLink]", host: { listeners: { "click": "onClick($event)" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[e11ExternalLink]',
                }]
        }], ctorParameters: () => [{ type: i0.ViewContainerRef }, { type: i0.ElementRef }], propDecorators: { onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class E11ExternalLinkModule {
    static forRoot(config) {
        return {
            ngModule: E11ExternalLinkModule,
            providers: [
                {
                    provide: BaseE11ExternalLinkConfigProvider,
                    useClass: config.configProvider,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkModule, declarations: [E11ExternalLinkComponent, E11ExternalLinkDirective], imports: [CommonModule, E11ModalModule, E11InputCheckboxModule, E11ButtonModule, E11UtilModule], exports: [E11ExternalLinkDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkModule, imports: [CommonModule, E11ModalModule, E11InputCheckboxModule, E11ButtonModule, E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11ExternalLinkModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11ExternalLinkComponent, E11ExternalLinkDirective],
                    imports: [CommonModule, E11ModalModule, E11InputCheckboxModule, E11ButtonModule, E11UtilModule],
                    exports: [E11ExternalLinkDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BaseE11ExternalLinkConfigProvider, E11ExternalLinkComponent, E11ExternalLinkDirective, E11ExternalLinkModule };
